import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { MapPin, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    organization: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({
        fullName: "",
        email: "",
        organization: "",
        subject: "",
        message: ""
      });
      setIsSubmitting(false);
    }, 1000);
  };
  return <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-tagblue via-tagblue-light to-tagblue text-white overflow-hidden">
        {/* Subtle 3D Background Elements */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-32 left-10 w-20 h-20 bg-white/10 rounded-2xl backdrop-blur-sm rotate-12 animate-float" />
          <div className="absolute top-20 right-20 w-16 h-16 bg-white/10 rounded-xl backdrop-blur-sm -rotate-12 animate-float" style={{
          animationDelay: '2s'
        }} />
          <div className="absolute bottom-32 left-20 w-12 h-12 bg-white/10 rounded-lg backdrop-blur-sm rotate-45 animate-float" style={{
          animationDelay: '4s'
        }} />
          <div className="absolute bottom-20 right-10 w-16 h-16 bg-white/10 rounded-xl backdrop-blur-sm -rotate-6 animate-float" style={{
          animationDelay: '1s'
        }} />
        </div>
        
        <div className="relative z-50">
          <Navbar />
        </div>
        <div className="container mx-auto px-4 py-16 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 animate-slide-up">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-blue-100 drop-shadow-2xl">
              Get in Touch
            </span>
          </h1>
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-4 border border-white/20 shadow-2xl animate-slide-up max-w-3xl mx-auto" style={{
          animationDelay: '0.2s'
        }}>
            <p className="text-white/95 text-lg leading-relaxed">
              Have questions for us? We're here to help you and simplify AI
              inclusion in Music Industry. Tagmusic is about collating all
              requirements related to AI in Music and test them practically
            </p>
          </div>
        </div>
      </div>

      {/* Contact Form Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-100 relative overflow-hidden">
        {/* Enhanced 3D Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-24 h-24 bg-tagblue/10 rounded-full blur-xl animate-float" />
          <div className="absolute bottom-10 left-10 w-32 h-32 bg-tagblue-light/10 rounded-full blur-xl animate-float" style={{
          animationDelay: '3s'
        }} />
          <div className="absolute top-32 left-20 w-20 h-20 bg-tagblue/15 rounded-3xl backdrop-blur-sm rotate-12 animate-float" style={{
          animationDelay: '1s'
        }} />
          <div className="absolute bottom-32 right-20 w-16 h-16 bg-tagblue-light/15 rounded-2xl backdrop-blur-sm -rotate-12 animate-float" style={{
          animationDelay: '2s'
        }} />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto group relative">
            {/* Card glow effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-tagblue to-tagblue-light rounded-3xl blur-2xl opacity-25 group-hover:opacity-40 transition-opacity duration-500" />
            
            {/* Main card */}
            <div className="relative bg-white/80 backdrop-blur-md rounded-3xl p-10 shadow-2xl border border-white/50 hover:shadow-3xl transition-all duration-500">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="fullName" className="block text-gray-700 font-medium">
                      Full Name
                    </label>
                    <Input id="fullName" name="fullName" value={formData.fullName} onChange={handleChange} placeholder="Enter your full name" required className="bg-white/70 backdrop-blur-sm border-gray-200 focus:border-tagblue" />
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="email" className="block text-gray-700 font-medium">
                      Email Address
                    </label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="your@email.com" required className="bg-white/70 backdrop-blur-sm border-gray-200 focus:border-tagblue" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="organization" className="block text-gray-700 font-medium">
                    Organization
                  </label>
                  <Input id="organization" name="organization" value={formData.organization} onChange={handleChange} placeholder="Your company or organization" className="bg-white/70 backdrop-blur-sm border-gray-200 focus:border-tagblue" />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="subject" className="block text-gray-700 font-medium">
                    Subject
                  </label>
                  <Input id="subject" name="subject" value={formData.subject} onChange={handleChange} placeholder="How can we help you?" required className="bg-white/70 backdrop-blur-sm border-gray-200 focus:border-tagblue" />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="message" className="block text-gray-700 font-medium">
                    Message
                  </label>
                  <Textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="Tell us more about your inquiry..." className="min-h-[150px] bg-white/70 backdrop-blur-sm border-gray-200 focus:border-tagblue" required />
                </div>
                
                <div className="text-center">
                  <Button type="submit" className="bg-gradient-to-r from-tagblue to-tagblue-dark hover:from-tagblue-dark hover:to-tagblue text-white px-8 py-3 rounded-2xl w-full md:w-auto shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300" disabled={isSubmitting}>
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </div>
              </form>
              
              {/* Decorative bottom accent */}
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-tagblue to-tagblue-light rounded-full opacity-60" />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="py-16 bg-gray-800 text-white relative overflow-hidden">
        {/* 3D Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-24 h-24 bg-tagblue/20 rounded-3xl backdrop-blur-sm rotate-12 animate-float" />
          <div className="absolute top-20 right-20 w-16 h-16 bg-tagblue-light/20 rounded-xl backdrop-blur-sm -rotate-12 animate-float" style={{
          animationDelay: '2s'
        }} />
          <div className="absolute bottom-20 left-20 w-12 h-12 bg-tagblue/20 rounded-lg backdrop-blur-sm rotate-45 animate-float" style={{
          animationDelay: '4s'
        }} />
          <div className="absolute bottom-10 right-10 w-20 h-20 bg-tagblue-light/20 rounded-xl backdrop-blur-sm -rotate-6 animate-float" style={{
          animationDelay: '1s'
        }} />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Location */}
            <div className="group relative text-center">
              {/* Card glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-tagblue to-tagblue-light rounded-3xl blur-2xl opacity-25 group-hover:opacity-50 transition-opacity duration-500" />
              
              {/* Main card */}
              <div className="relative bg-white/5 backdrop-blur-md p-8 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-white/10 group-hover:scale-105 transform">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-tagblue to-tagblue-dark rounded-xl flex items-center justify-center shadow-lg">
                    <MapPin className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-4 text-white">Our Location</h3>
                <p className="text-gray-300 leading-relaxed">
                  16 Green Ash Street, Monroe,<br />
                  New Jersey, NJ 08831
                </p>
                
                {/* Decorative bottom accent */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-tagblue to-tagblue-light rounded-full opacity-60" />
              </div>
            </div>
            
            {/* Email */}
            <div className="group relative text-center" style={{
            animationDelay: '0.2s'
          }}>
              {/* Card glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-tagblue-light to-tagblue rounded-3xl blur-2xl opacity-25 group-hover:opacity-50 transition-opacity duration-500" />
              
              {/* Main card */}
              <div className="relative bg-white/5 backdrop-blur-md p-8 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-white/10 group-hover:scale-105 transform">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-tagblue-light to-tagblue rounded-xl flex items-center justify-center shadow-lg">
                    <Mail className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-4 text-white">Email Us</h3>
                <p className="text-gray-300 leading-relaxed">
                  <a href="mailto:contact@vlinder.io" className="text-white hover:text-tagblue-light transition-colors">utkarsh.sharma@vlinder.io</a>
                </p>
                
                {/* Decorative bottom accent */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-tagblue-light to-tagblue rounded-full opacity-60" />
              </div>
            </div>
            
            {/* Phone */}
            <div className="group relative text-center" style={{
            animationDelay: '0.4s'
          }}>
              {/* Card glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-tagblue to-tagblue-light rounded-3xl blur-2xl opacity-25 group-hover:opacity-50 transition-opacity duration-500" />
              
              {/* Main card */}
              <div className="relative bg-white/5 backdrop-blur-md p-8 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-white/10 group-hover:scale-105 transform">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-tagblue to-tagblue-dark rounded-xl flex items-center justify-center shadow-lg">
                    <Phone className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-4 text-white">Call Us</h3>
                <p className="text-gray-300 leading-relaxed">
                  <a href="tel:+17047716811" className="text-white hover:text-tagblue-light transition-colors">
                    +1 704 771 6811
                  </a>
                </p>
                
                {/* Decorative bottom accent */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-tagblue to-tagblue-light rounded-full opacity-60" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>;
};
export default Contact;